/**
 * ══ NGÀY THEO GIỜ VIỆT NAM ════════════════════════════════════════════════════════════════════
 * Gốc: `proship-trans/src/lib/util.ts:20–41`. LỆCH BẢN GỐC CÓ CHỦ Ý theo **E21** (chốt 04/09):
 * nhánh lỗi KHÔNG lùi về giờ máy. Bản cũ nuốt lỗi — `toISODate(new Date('rác'))` trả
 * `'NaN-NaN-NaN'`, một "ngày" giả đi thẳng vào tầng ghi sổ. Ở đây, hỏng thì NÉM LỖI, không âm
 * thầm trả dữ liệu sai.
 * KHÔNG dùng giờ UTC (lệch 1 ngày trong khung 0h–7h sáng VN) và CŨNG KHÔNG dựa vào giờ máy
 * (máy đặt nhầm múi giờ là cả hệ thống lệch ngày). Hàm này NEO CỨNG Asia/Ho_Chi_Minh.
 *
 * Hai nguyên nhân lỗi KHÁC NHAU, phải báo ĐÚNG nguyên nhân, không gộp chung:
 *  1. Date đầu vào không hợp lệ (`Number.isNaN(d.getTime())`) — lỗi ở NGƯỜI GỌI.
 *  2. Máy chủ thiếu dữ liệu múi giờ ICU cho Asia/Ho_Chi_Minh — lỗi CẤU HÌNH máy chủ. Formatter
 *     phải dựng BÊN TRONG try của từng hàm (không dựng ở phạm vi module) — dựng ở ngoài thì lỗi
 *     ICU nổ ngay lúc import, không bao giờ chạm được nhánh catch của hàm.
 */
const VN_TZ = 'Asia/Ho_Chi_Minh'

/** Trả 'YYYY-MM-DD' theo giờ VN. */
export const toISODate = (d: Date = new Date()): string => {
  if (Number.isNaN(d.getTime())) {
    throw new Error('Date đầu vào không hợp lệ — không tính được ngày theo giờ Việt Nam (Asia/Ho_Chi_Minh).')
  }
  try {
    // en-CA cho đúng khuôn YYYY-MM-DD
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: VN_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d)
  } catch (e) {
    throw new Error(
      'Máy chủ thiếu dữ liệu múi giờ (ICU) cho Asia/Ho_Chi_Minh — đây là LỖI CẤU HÌNH, không được ' +
      'âm thầm trả về ngày sai.',
      { cause: e },
    )
  }
}

/** GIỜ:PHÚT theo giờ VN. */
export const toVNTime = (d: Date = new Date()): string => {
  if (Number.isNaN(d.getTime())) {
    throw new Error('Date đầu vào không hợp lệ — không tính được giờ theo giờ Việt Nam (Asia/Ho_Chi_Minh).')
  }
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: VN_TZ, hour: '2-digit', minute: '2-digit' }).format(d)
  } catch (e) {
    throw new Error(
      'Máy chủ thiếu dữ liệu múi giờ (ICU) cho Asia/Ho_Chi_Minh — đây là LỖI CẤU HÌNH, không được ' +
      'âm thầm trả về giờ sai.',
      { cause: e },
    )
  }
}

/**
 * Cộng (hoặc trừ) số ngày vào một chuỗi `YYYY-MM-DD`, trả lại đúng khuôn đó.
 *
 * ⚠ ĐÂY LÀ PHÉP LỊCH THUẦN, KHÔNG DÍNH MÚI GIỜ — và vì thế nó KHÔNG dùng `toISODate` ở trên.
 *   Đầu vào đã là một NGÀY rồi, không phải một thời điểm; "ngày 3 tháng 9 cộng 5" ra cùng một
 *   đáp số ở mọi múi giờ. Neo vào UTC chỉ để `Date` làm hộ việc đổi tháng và năm nhuận, rồi đọc
 *   lại bằng `getUTC*` nên không có bước đổi múi giờ nào xảy ra.
 * ⚠ Cố ý KHÔNG dùng `toISOString()`: nó bị lint chặn vì hay bị dùng nhầm cho thời điểm, và ở đây
 *   ghép chuỗi bằng tay vừa rõ vừa khỏi phải giải thích ngoại lệ.
 *
 * Chuỗi không đúng khuôn `YYYY-MM-DD` ⇒ NÉM LỖI, cùng luật với `toISODate` (E21): không âm thầm
 * trả một ngày sai.
 */
export const addDaysIso = (iso: string, days: number): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (m === null) {
    throw new Error(`Ngày "${iso}" không đúng khuôn YYYY-MM-DD — không cộng ngày được.`)
  }
  const [y, mo, day] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const d = new Date(Date.UTC(y, mo - 1, day))
  /* ⚠ `Date.UTC` KHÔNG KÊU khi ngày không có thật — nó tự trôi: `Date.UTC(2026, 1, 31)` ra ngày
     3 tháng 3. Im lặng và sai, đúng họ lỗi E21. `Number.isNaN` cũng không bắt được vì kết quả
     vẫn là một thời điểm hợp lệ. Cách duy nhất chắc chắn là ĐỌC LẠI và so với đầu vào. */
  if (d.getUTCFullYear() !== y || d.getUTCMonth() !== mo - 1 || d.getUTCDate() !== day) {
    throw new Error(`Ngày "${iso}" không có thật — không cộng ngày được.`)
  }
  d.setUTCDate(d.getUTCDate() + days)
  const p2 = (n: number): string => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}`
}
